import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createHeadingNode, $createQuoteNode, type HeadingTagType } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { $findMatchingParent } from '@lexical/utils'
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  REDO_COMMAND,
  UNDO_COMMAND
} from 'lexical'
import React from 'react'
import { DEFAULT_TOOLBAR, isHeadingTag, type ToolbarOptions } from '../text-editor.types'

import { BLOCK_TYPES, ICONS, TEXT_ALIGNMENTS } from '../constants'
import { useToolbarState } from '../hooks/use-toolbar-state.hook'
import { sanitizeUrl } from '../sanitize-url'
import { FontSizeStepper } from './FontSizeStepper'

function toggleLink(editor: LexicalEditor) {
  let currentUrl = ''
  editor.getEditorState().read(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return

    const node = selection.anchor.getNode()
    const link = $isLinkNode(node) ? node : $findMatchingParent(node, $isLinkNode)
    if ($isLinkNode(link)) currentUrl = link.getURL()
  })

  const input = window.prompt('Link URL', currentUrl || 'https://')
  if (input === null) return

  const trimmed = input.trim()
  if (trimmed === '' || trimmed === 'https://') {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    return
  }

  const url = sanitizeUrl(trimmed)
  if (!url) {
    window.alert('Enter a valid URL (http, https, mailto or tel).')
    return
  }

  editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
}

function formatParagraph(editor: LexicalEditor) {
  editor.update(() => {
    const selection = $getSelection()
    $setBlocksType(selection, () => $createParagraphNode())
  })
}

function formatHeading(editor: LexicalEditor, headingTag: HeadingTagType) {
  editor.update(() => {
    const selection = $getSelection()
    $setBlocksType(selection, () => $createHeadingNode(headingTag))
  })
}

function formatQuote(editor: LexicalEditor) {
  editor.update(() => {
    const selection = $getSelection()
    $setBlocksType(selection, () => $createQuoteNode())
  })
}

function handleBlockType(editor: LexicalEditor, type: string) {
  if (type === 'paragraph') return formatParagraph(editor)
  if (type === 'quote') return formatQuote(editor)
  if (isHeadingTag(type)) return formatHeading(editor, type)

  throw new Error(`Unknown block type: ${type}`)
}

function maskStyle(url: string): React.CSSProperties {
  return {
    WebkitMaskImage: `url('${url}')`,
    WebkitMaskPosition: 'center',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskSize: 'contain',
    maskImage: `url('${url}')`,
    maskPosition: 'center',
    maskRepeat: 'no-repeat',
    maskSize: 'contain'
  }
}

const ToolbarGroup = ({ children }: { children: React.ReactNode }) => {
  return <div className="form__toolbar-group">{children}</div>
}

export const Toolbar = ({ options }: { options?: ToolbarOptions }) => {
  const show = { ...DEFAULT_TOOLBAR, ...options }
  const [editor] = useLexicalComposerContext()
  const {
    canUndo,
    canRedo,
    blockType,
    isBold,
    isItalic,
    isUnderline,
    isLink,
    fontSize,
    elementFormat
  } = useToolbarState(editor)

  const btnClass = (active = false) => `btn btn-sm !px-2 ${active ? 'btn-default' : 'btn-ghost'}`
  const iconClass = 'icon flex h-5 w-5 shrink-0 bg-current'
  const showEmphasis = show.bold || show.italic || show.underline || show.link

  return (
    <div className="form__toolbar">
      {show.history && (
        <ToolbarGroup>
          <button
            type="button"
            disabled={!canUndo}
            onClick={() => {
              editor.dispatchCommand(UNDO_COMMAND, undefined)
            }}
            className={btnClass()}
            aria-label="Undo"
          >
            <i className={iconClass} style={maskStyle(ICONS.undo)} />
          </button>

          <button
            type="button"
            disabled={!canRedo}
            onClick={() => {
              editor.dispatchCommand(REDO_COMMAND, undefined)
            }}
            className={btnClass()}
            aria-label="Redo"
          >
            <i className={iconClass} style={maskStyle(ICONS.redo)} />
          </button>
        </ToolbarGroup>
      )}

      {show.blockType && (
        <ToolbarGroup>
          <select
            className="form__field form__field-sm w-auto pe-9"
            value={blockType}
            onChange={(e) => handleBlockType(editor, e.target.value)}
            aria-label="Block type"
          >
            {BLOCK_TYPES.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </ToolbarGroup>
      )}

      {show.fontSize && (
        <ToolbarGroup>
          <FontSizeStepper fontSize={fontSize} />
        </ToolbarGroup>
      )}

      {showEmphasis && (
        <ToolbarGroup>
          {show.bold && (
            <button
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
              }}
              className={btnClass(isBold)}
              aria-label="Format Bold"
              aria-pressed={isBold}
            >
              <i className={iconClass} style={maskStyle(ICONS.bold)} />
            </button>
          )}

          {show.italic && (
            <button
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
              }}
              className={btnClass(isItalic)}
              aria-label="Format Italics"
              aria-pressed={isItalic}
            >
              <i className={iconClass} style={maskStyle(ICONS.italic)} />
            </button>
          )}

          {show.underline && (
            <button
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
              }}
              className={btnClass(isUnderline)}
              aria-label="Format Underline"
              aria-pressed={isUnderline}
            >
              <i className={iconClass} style={maskStyle(ICONS.underline)} />
            </button>
          )}

          {show.link && (
            <button
              type="button"
              onClick={() => toggleLink(editor)}
              className={btnClass(isLink)}
              aria-label="Insert Link"
              aria-pressed={isLink}
            >
              <i className={iconClass} style={maskStyle(ICONS.link)} />
            </button>
          )}
        </ToolbarGroup>
      )}

      {show.alignment && (
        <ToolbarGroup>
          {TEXT_ALIGNMENTS.map(({ value, icon, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value)
              }}
              className={btnClass(elementFormat === value)}
              aria-label={label}
              aria-pressed={elementFormat === value}
            >
              <i className={iconClass} style={maskStyle(icon)} />
            </button>
          ))}
        </ToolbarGroup>
      )}
    </div>
  )
}
